import dynamic from 'next/dynamic';
import React, { useRef } from 'react';
import { GlobalHeader } from '@/components/Layouts';
import type { MarketplaceNavItem } from '@/components/Layouts';
const GlobalFooter = dynamic(() => import('@/components/Layouts/GlobalFooter'), {
  ssr: true,
});
import { useConfigProvider } from '@/context/ConfigProvider';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { EventName } from '@/shared/event-types';
import { imgUrl } from '@/shared/utils';
import Image from 'next/image';
import { useRouter } from 'next/router';

// 导入菜单图标
import iconContentProtection from '@/components/Layouts/HeaderDropdown/icons/icon-content-protection.svg';
import iconFakeAccount from '@/components/Layouts/HeaderDropdown/icons/icon-remove-fake-account.svg';
import iconDMCA from '@/components/Layouts/HeaderDropdown/icons/icon-dmca-takedown.svg';
import iconPhotoProtection from '@/components/Layouts/HeaderDropdown/icons/icon-photo-exposure-protection.svg';
import iconCreator from '@/components/Layouts/HeaderDropdown/icons/icon-creator.svg';
import icontwitter from '@/components/Layouts/HeaderDropdown/icons/icon-twitter.svg';
import iconInstagram from '@/components/Layouts/HeaderDropdown/icons/icon-instagram.svg';
import iconTiktok from '@/components/Layouts/HeaderDropdown/icons/icon-tiktok.svg';
import iconCompare from '@/components/Layouts/HeaderDropdown/icons/icon-compare.svg';
import iconWordsChecker from '@/components/Layouts/HeaderDropdown/icons/icon-words-checker.svg';
import iconCaptionGenerator from '@/components/Layouts/HeaderDropdown/icons/icon-caption-generator.svg';
import iconIndividuals from '@/components/Layouts/HeaderDropdown/icons/icon-individuals.svg';
import iconUsernameSearch from '@/components/Layouts/HeaderDropdown/icons/icon-username-search.svg';
import iconVideoSearch from '@/components/Layouts/HeaderDropdown/icons/icon-video-search.svg';
import iconPhotoSearch from '@/components/Layouts/HeaderDropdown/icons/icon-photo-search.svg';
import iconFaceSearch from '@/components/Layouts/HeaderDropdown/icons/icon-face-search.svg';
import iconGeneral from '@/components/Layouts/HeaderDropdown/icons/icon-general.svg';
import iconBlog from '@/components/Layouts/HeaderDropdown/icons/icon-blog.svg';
import iconGuide from '@/components/Layouts/HeaderDropdown/icons/icon-guide.svg';
import iconAffiliate from '@/components/Layouts/HeaderDropdown/icons/icon-affiliate.svg';
import iconEmail from '@/components/Layouts/HeaderDropdown/icons/icon-email.svg';
import iconX from '@/components/Layouts/HeaderDropdown/icons/icon-x.svg';
import iconTelegram from '@/components/Layouts/HeaderDropdown/icons/icon-telegram.svg';
import iconRevengePorn from '@/components/Layouts/HeaderDropdown/icons/icon-revenge-porn.svg';
import iconAIImageAbuseDetection from '@/components/Layouts/HeaderDropdown/icons/icon-ai-image-abuse-detection.svg';

interface LayoutProps {
  head?: React.ReactNode;
  children: React.ReactNode;
  showHeader?: boolean;
  showFooter?: boolean;
}

const Layout = ({ children, head }: LayoutProps) => {
  const { isMobile } = useConfigProvider();
  const { t } = useTranslation('common');
  const ref = useRef(null);
  const router = useRouter();

  // 不显示背景图的路由
  const hideBackgroundRoutes = ['/shadowban-test'];
  const shouldShowBackground = !hideBackgroundRoutes.some((route) => router.pathname.startsWith(route));

  const getNavItems = (): MarketplaceNavItem[] => {
    return [
      // Solutions 菜单 - 双列布局
      {
        text: t('Solutions'),
        gaName: EventName.header_service,
        dropdownConfig: {
          layout: 'columns',
          groups: [
            {
              title: t('For Creator'),
              items: [
                {
                  text: t('Fake Account Detection & Removal'),
                  path: '/remove-fake-account',
                  icon: iconFakeAccount,
                  gaName: EventName.header_remove_fake_account,
                },
                {
                  text: t('DMCA Takedown Service'),
                  path: '/dmca-takedown-service',
                  icon: iconDMCA,
                  gaName: EventName.header_dmca_takedown,
                },
                {
                  text: t('Onlyfans Creator Protection'),
                  path: '/remove-leaked-onlyfans-content',
                  icon: iconContentProtection,
                  gaName: EventName.header_onlyfans_protection,
                },
              ],
            },
            {
              title: t('For Individuals'),
              items: [
                {
                  text: t('Private Photo Protection'),
                  path: '/leaked-private-photos',
                  icon: iconPhotoProtection,
                  gaName: EventName.header_photo_protection,
                },
                {
                  text: t('Revenge Porn Removal'),
                  path: '/find-and-remove-revenge-porn',
                  icon: iconRevengePorn,
                  gaName: EventName.header_find_and_remove_revenge_porn,
                },
                {
                  text: t('AI Image Abuse Detection'),
                  path: '/ai-porn-detection-removal',
                  icon: iconAIImageAbuseDetection,
                  gaName: EventName.header_ai_image_abuse_detection,
                },
              ],
            },
          ],
        },
      },
      // Pricing 菜单 - 无下拉
      {
        text: t('Pricing'),
        path: '/plan',
        gaName: EventName.view_pricing,
      },
      // Tools 菜单 - 标签页布局
      {
        text: t('Tools'),
        gaName: EventName.header_tool,
        dropdownConfig: {
          layout: 'tabs',
          tabs: [
            {
              key: 'creator',
              label: t('For Creator'),
              icon: iconCreator,
              sections: [
                {
                  title: t('Shadowban Test'),
                  path: '/shadowban-test',
                  items: [
                    {
                      text: t('Twitter Shadowban Test'),
                      path: '/shadowban-test/twitter-shadowban-test',
                      icon: icontwitter,
                    },
                    {
                      text: t('Instagram Shadowban Test'),
                      path: '/shadowban-test/instagram-shadowban-test',
                      icon: iconInstagram,
                    },
                    {
                      text: t('Tiktok Shadowban Test'),
                      path: '/shadowban-test/tiktok-shadowban-test',
                      icon: iconTiktok,
                    },
                  ],
                },
                {
                  title: t('Platform Guides'),
                  items: [
                    {
                      text: t('Creator Platform Comparison Tool'),
                      path: '/compare',
                      icon: iconCompare,
                    },
                    {
                      text: t('OnlyFans Restricted Words Checker'),
                      path: '/onlyfans-restricted-words-checker',
                      icon: iconWordsChecker,
                    },
                    {
                      text: t('OnlyFans Caption Generator'),
                      path: '/onlyfans-caption-generator',
                      icon: iconCaptionGenerator,
                    },
                  ],
                },
              ],
            },
            {
              key: 'individuals',
              label: t('For Individuals'),
              icon: iconIndividuals,
              sections: [
                {
                  title: t('Reverse Search'),
                  path: '/content-monitoring',
                  items: [
                    {
                      text: t('Reverse Username Search'),
                      path: '/content-monitoring/reverse-username-search',
                      icon: iconUsernameSearch,
                    },
                    {
                      text: t('Reverse Video Search'),
                      path: '/content-monitoring/reverse-video-search',
                      icon: iconVideoSearch,
                    },
                    {
                      text: t('Reverse Photo Search'),
                      path: '/content-monitoring/reverse-photo-search',
                      icon: iconPhotoSearch,
                    },
                    {
                      text: t('Reverse Face Search'),
                      path: '/content-monitoring/reverse-face-search',
                      icon: iconFaceSearch,
                    },
                  ],
                },
              ],
            },
            {
              key: 'general',
              label: t('General'),
              icon: iconGeneral,
              sections: [
                {
                  title: t('DMCA Takedown Notice Generator'),
                  path: '/dmca-takedown',
                  items: [],
                },
              ],
            },
          ],
        },
      },
      // Resources 菜单 - 简单列表
      {
        text: t('Resources'),
        gaName: EventName.header_tool,
        dropdownConfig: {
          layout: 'simple',
          groups: [
            {
              title: t('Resources'),
              items: [
                {
                  text: t('Blog'),
                  path: '/blog',
                  icon: iconBlog,
                  gaName: EventName.header_tool,
                },
                {
                  text: t('DMCA Guides'),
                  path: '/guide',
                  icon: iconGuide,
                  gaName: EventName.header_tool,
                },
              ],
            },
            {
              title: t('Cooperation'),
              items: [
                {
                  text: t('Affiliate Program'),
                  path: '/affiliate-program',
                  icon: iconAffiliate,
                  gaName: EventName.header_affiliate,
                },
              ],
            },
          ],
        },
      },
      // Contact 菜单 - 简单列表
      {
        text: t('Contact'),
        gaName: EventName.header_contact,
        dropdownConfig: {
          layout: 'simple',
          groups: [
            {
              title: '',
              items: [
                {
                  text: t('Email'),
                  path: 'mailto:erasaprotection@erasa.net',
                  icon: iconEmail,
                  isExternal: true,
                },
                {
                  text: 'X(Twitter)',
                  path: 'https://x.com/Erasaprotect',
                  icon: iconX,
                  isExternal: true,
                },
                {
                  text: 'Telegram',
                  path: 'https://t.me/erasa_emliy',
                  icon: iconTelegram,
                  isExternal: true,
                },
              ],
            },
          ],
        },
      },
    ];
  };

  return (
    <div className={'layout'}>
      {head}
      {shouldShowBackground && (
        <Image
          src={imgUrl('https://res-front.pumpsoul.com/prod-erasa/image/background-icon.webp', 'mid')}
          alt="background"
          width={100}
          priority
          height={100}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: -2,
          }}
        />
      )}
      <header className={'header'}>
        <GlobalHeader isMobile={isMobile} navItems={[...getNavItems()]} />
      </header>
      <main
        ref={ref}
        style={{
          marginTop: 'calc(var(--pietra-global-header-heigth))',
        }}
        className={classNames('main', {
          mobile: isMobile,
        })}
      >
        <div>{children}</div>
      </main>
      <footer className={'footer'}>
        <GlobalFooter />
      </footer>
    </div>
  );
};
export default Layout;
